import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Wallet, Zap, CheckCircle2, ArrowDownToLine, Clock } from "lucide-react";

export default function OwnerWithdraw() {
  const [data, setData] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [processing, setProcessing] = useState(false);

  const load = () => api.get("/owner/withdrawals").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const doWithdraw = async () => {
    setProcessing(true);
    try {
      const { data: w } = await api.post("/owner/withdrawals/instant");
      toast.success(`Withdrawal request placed · ₹${Math.round(w.net_amount).toLocaleString("en-IN")} · Ref ${w.reference}`);
      setConfirmOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Withdrawal failed");
    } finally {
      setProcessing(false);
    }
  };

  if (!data) return null;
  const net = Math.max(0, data.available_balance - data.instant_fee);
  const canWithdraw = data.available_balance > data.instant_fee;

  return (
    <section className="mt-10">
      <div className="text-xs tracking-[0.25em] uppercase font-bold text-amber-600">Payouts</div>
      <h2 className="font-display font-black text-3xl tracking-tighter mt-1 flex items-center gap-2">
        <Wallet size={26} className="text-orange-800" /> Withdraw your earnings
      </h2>

      <div className="mt-5 bg-white border border-slate-200 rounded-2xl p-6 grid md:grid-cols-[1fr_auto] gap-6 items-center">
        <div>
          <div className="text-[10px] tracking-widest uppercase font-bold text-slate-500">Available balance</div>
          <div className="mt-1 font-display font-black text-4xl">
            ₹{Math.round(data.available_balance).toLocaleString("en-IN")}
          </div>
          <div className="mt-1 text-xs text-slate-500">
            From {data.unsettled_bookings} paid booking{data.unsettled_bookings === 1 ? "" : "s"} · Transfer fee ₹{data.instant_fee}
            {canWithdraw && <> · You'll receive <span className="font-bold text-emerald-700">₹{Math.round(net).toLocaleString("en-IN")}</span></>}
          </div>
          <div className="mt-3 text-[11px] leading-relaxed text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-2.5">
            💡 <b>Manual payout:</b> After you request, TheHostelNow admin transfers the amount to your verified bank/UPI via IMPS (usually within 24 hrs on business days) and marks it paid with a UTR reference. You are not paid via Razorpay directly — Razorpay only receives student payments.
          </div>
        </div>

        <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="withdraw-btn"
              disabled={!canWithdraw}
              className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white h-12 px-6 gap-2 disabled:opacity-50 active:scale-95 transition-transform"
            >
              <Zap size={16} /> Request payout
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl flex items-center gap-2">
                <ArrowDownToLine className="text-emerald-600" /> Confirm payout request
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between border-b border-dashed border-slate-200 pb-2">
                <span className="text-slate-600">Available balance</span>
                <span className="font-semibold">₹{Math.round(data.available_balance).toLocaleString("en-IN")}</span>
              </div>
              <div className="flex justify-between border-b border-dashed border-slate-200 pb-2">
                <span className="text-slate-600">Transfer fee</span>
                <span className="font-semibold text-rose-600">−₹{data.instant_fee}</span>
              </div>
              <div className="flex justify-between font-display font-black text-xl pt-2">
                <span>You'll receive</span>
                <span className="text-emerald-700">₹{Math.round(net).toLocaleString("en-IN")}</span>
              </div>
              <p className="text-xs text-slate-500 pt-3 border-t border-slate-100">
                Payout is transferred to your verified bank via IMPS by the HostelNow team. Typically settled within 24 hours on business days. You'll see a UTR reference once paid.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmOpen(false)} className="rounded-full">Cancel</Button>
              <Button
                data-testid="withdraw-confirm"
                onClick={doWithdraw}
                disabled={processing}
                className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {processing ? "Placing…" : "Confirm & request payout"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {data.withdrawals.length > 0 && (
        <div className="mt-4 bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-2 text-xs tracking-widest uppercase font-bold text-slate-600">
            <Clock size={14} /> Recent withdrawals
          </div>
          <div className="divide-y divide-slate-100">
            {data.withdrawals.slice(0, 5).map((w) => {
              const isPaid = w.status === "processed";
              const isRejected = w.status === "rejected";
              return (
                <div key={w.id} data-testid={`withdrawal-${w.id}`} className="px-6 py-3 flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold">₹{Math.round(w.net_amount).toLocaleString("en-IN")} → {w.destination?.ifsc} · A/C ····{w.destination?.account_number_last4}</div>
                    <div className="text-xs text-slate-500">
                      {new Date(w.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                      · {w.bookings_count} bookings · Ref {w.reference}
                      {isPaid && w.utr && <> · <span className="font-mono">UTR {w.utr}</span></>}
                      {isRejected && w.rejection_reason && <> · <span className="text-rose-700">{w.rejection_reason}</span></>}
                    </div>
                  </div>
                  <div className={`flex items-center gap-1 text-xs font-semibold ${isPaid ? "text-emerald-700" : isRejected ? "text-rose-700" : "text-amber-700"}`}>
                    {isPaid ? <CheckCircle2 size={14} /> : <Clock size={14} />} {w.status}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!canWithdraw && data.available_balance === 0 && (
        <p className="mt-2 text-xs text-slate-500">
          No earnings yet. Once students pay for their stay at your hostels, you can withdraw here.
        </p>
      )}
    </section>
  );
}
